import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { getMemberCurrentPlanTiming } from '@/services/technical';
import styles from './PlanPresentation.less';

export function readPlanTiming(response, memberId) {
  const data = response?.data;
  if (
    !['00000', '0'].includes(String(response?.code)) ||
    String(data?.memberId) !== String(memberId) ||
    !Array.isArray(data?.weekdays) ||
    data.weekdays.length > 7 ||
    new Set(data.weekdays).size !== data.weekdays.length ||
    data.weekdays.some((day) => !Number.isInteger(day) || day < 1 || day > 7) ||
    !['ENABLED', 'DISABLED', 'NOT_CONFIGURED', 'UNKNOWN'].includes(
      data.reminderStatus,
    ) ||
    (data.timeZone != null &&
      (typeof data.timeZone !== 'string' || data.timeZone.length > 80)) ||
    (data.reminderTime != null &&
      (typeof data.reminderTime !== 'string' ||
        !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(data.reminderTime)))
  )
    throw new Error('Invalid plan timing');
  return data;
}

export default function CurrentPlanTiming({ memberId }) {
  const [state, setState] = useState({});
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  useEffect(() => {
    const request = ++generation.current;
    setState({ memberId, loading: true });
    getMemberCurrentPlanTiming({ requestData: { memberId } })
      .then((response) => {
        const data = readPlanTiming(response, memberId);
        if (request === generation.current) setState({ memberId, data });
      })
      .catch((error) => {
        if (request !== generation.current) return;
        const status = error?.response?.status;
        setState({
          memberId,
          error:
            status === 401
              ? '请先登录后台'
              : status === 403
              ? '无会员档案菜单权限'
              : '训练日和提醒暂时无法加载',
        });
      });
    return () => {
      generation.current += 1;
    };
  }, [memberId, revision]);
  const current = String(state.memberId) === String(memberId) ? state : {};
  if (!current.data)
    return (
      <div
        className={`${styles.timingState} ${
          current.error ? styles.timingStateError : ''
        }`}
      >
        <span>{current.error || '加载训练日和提醒…'}</span>
        {current.error && (
          <Button
            type="link"
            size="small"
            onClick={() => setRevision((value) => value + 1)}
          >
            重试
          </Button>
        )}
      </div>
    );
  const { weekdays, reminderStatus, reminderTime, timeZone } = current.data;
  const dayNames = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  const displayedWeekdays = [...weekdays]
    .sort((a, b) => a - b)
    .map((day) => ({ day, name: dayNames[day - 1] }));
  const reminder =
    reminderStatus === 'ENABLED'
      ? `已开启 · ${reminderTime || '时间未提供'}`
      : { DISABLED: '已关闭', NOT_CONFIGURED: '未配置', UNKNOWN: '状态未提供' }[
          reminderStatus
        ];
  return (
    <div className={styles.timing}>
      <div className={styles.timingBlock}>
        <span className={styles.timingLabel}>每周训练日</span>
        <div
          className={styles.timingDays}
          aria-label={
            displayedWeekdays.length
              ? `训练日：${displayedWeekdays
                  .map(({ name }) => name)
                  .join('、')}`
              : '训练日未设置'
          }
        >
          {displayedWeekdays.length ? (
            displayedWeekdays.map(({ day, name }) => (
              <strong key={day}>{name}</strong>
            ))
          ) : (
            <span className={styles.timingMuted}>未设置</span>
          )}
        </div>
      </div>
      <div className={styles.timingBlock}>
        <span className={styles.timingLabel}>运动提醒</span>
        <div className={styles.timingReminder}>
          <span
            aria-hidden="true"
            className={
              reminderStatus === 'ENABLED'
                ? styles.timingReminderEnabled
                : styles.timingReminderInactive
            }
          />
          <strong>{reminder}</strong>
          <small>会员时区 {timeZone || '未提供'}</small>
        </div>
      </div>
    </div>
  );
}
CurrentPlanTiming.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};
